/** Pure presentation of the proposal's pinned snapshots; never read live state. */
export function proposalRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Match transport and secret fields in both snake_case and camelCase. */
export function isInternalProposalField(key: string): boolean {
  const normalized = key.replace(/([a-z0-9])([A-Z])/g, "$1_$2");
  return /^(?:_|kind$|artifact_key$|changed_fields$|operation$|op$|representation$)|(?:^|_)(?:id|ids|ref|refs|token|version|checksum)$|(?:^|_)(?:credential|secret|authorization|password)(?:_|$)/i.test(
    normalized,
  );
}

export function proposalFields(value: Record<string, unknown>) {
  return Object.entries(value).filter(([key]) => !isInternalProposalField(key));
}

export function proposalFieldLabel(key: string): string {
  const label = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ");
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/**
 * Name a row from its OWN content.
 *
 * `text`, `process` and `category` joined the list for PB-4923: the same
 * heuristic now names the item a Player AI skip refers to ("the 'Machine
 * calibration' cause"), and those three are how a fishbone cause, a SIPOC step
 * and a fishbone branch carry their human text. Widening the list is safe by
 * construction — the first key that holds non-blank text wins, and the
 * numbered fallback is unchanged.
 */
export function proposalRowLabel(value: unknown, index: number): string {
  const row = proposalRecord(value);
  for (const key of [
    "title",
    "label",
    "name",
    "item",
    "action",
    "problem",
    "text",
    "process",
    "category",
  ]) {
    const label = row?.[key];
    if (typeof label === "string" && label.trim()) return label;
  }
  return `Item ${index + 1}`;
}

export interface ProposalDifference {
  path: string;
  operation: "Added" | "Removed" | "Changed";
  before?: unknown;
  after?: unknown;
}

function equal(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => equal(v, b[i]));
  }
  const left = proposalRecord(a);
  const right = proposalRecord(b);
  return (
    !!left &&
    !!right &&
    Object.keys(left).length === Object.keys(right).length &&
    Object.keys(left).every(
      (key) => Object.hasOwn(right, key) && equal(left[key], right[key]),
    )
  );
}

function arrayKey(before: unknown[], after: unknown[]): string | undefined {
  return ["id", "label"].find((key) =>
    [before, after].every((rows) => {
      const values = rows.map((row) => proposalRecord(row)?.[key]);
      return (
        values.every(
          (value) => typeof value === "string" && value.length > 0,
        ) && new Set(values).size === values.length
      );
    }),
  );
}

export function proposalDifferences(
  before: unknown,
  after: unknown,
  path = "Content",
  depth = 0,
): ProposalDifference[] {
  if (equal(before, after)) return [];
  if (depth < 10) {
    if (Array.isArray(before) && Array.isArray(after)) {
      const key = arrayKey(before, after);
      if (key) {
        const previous = new Map(
          before.map((row) => [proposalRecord(row)?.[key], row]),
        );
        const next = new Map(
          after.map((row) => [proposalRecord(row)?.[key], row]),
        );
        const changes = after.flatMap((row, i) =>
          proposalDifferences(
            previous.get(proposalRecord(row)?.[key]),
            row,
            `${path} · ${proposalRowLabel(row, i)}`,
            depth + 1,
          ),
        );
        before.forEach((row, i) => {
          if (!next.has(proposalRecord(row)?.[key]))
            changes.push({
              path: `${path} · ${proposalRowLabel(row, i)}`,
              operation: "Removed",
              before: row,
            });
        });
        const oldOrder = [...previous.keys()].filter((id) => next.has(id));
        const newOrder = [...next.keys()].filter((id) => previous.has(id));
        if (!equal(oldOrder, newOrder))
          changes.push({
            path: `${path} · Order`,
            operation: "Changed",
            before: before.map(proposalRowLabel),
            after: after.map(proposalRowLabel),
          });
        return changes;
      }
      // Without stable row keys, show the collection comparison, not an invented match.
    } else {
      const left = proposalRecord(before);
      const right = proposalRecord(after);
      if (left && right) {
        const keys = new Set(
          [...proposalFields(left), ...proposalFields(right)].map(
            ([key]) => key,
          ),
        );
        return [...keys].flatMap((key) =>
          proposalDifferences(
            left[key],
            right[key],
            path === "Content"
              ? proposalFieldLabel(key)
              : `${path} · ${proposalFieldLabel(key)}`,
            depth + 1,
          ),
        );
      }
    }
  }
  return [
    {
      path,
      operation:
        before === undefined
          ? "Added"
          : after === undefined
            ? "Removed"
            : "Changed",
      before,
      after,
    },
  ];
}
